<template>
  <DsSection
    v-if="chapters && chapters.length"
    v-resize-awds="handleResize"
    :with-space="false"
    class="kh-chapters ds-padding-y-24 ds-md-padding-y-48"
  >
    <!-- Tags & Summary tooggle -->
    <div
      v-if="sidebarTags.length"
      class="kh-chapters-tags ds-grid ds-padding-y-24 ds-md-padding-y-48"
    >
      <div
        class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--offset-2"
        :class="{
          'ds-md-grid__col--8 ds-lg-grid__col--6': showSummaryToogle,
          'ds-lg-grid__col--8': !showSummaryToogle
        }"
      >
        <DsTag
          v-for="(tag, index) in sidebarTags"
          :key="`sidebarTags-${index}`"
          :link="tag.link"
          :label="tag.name"
          color="green"
          class="ds-margin-right-8 ds-margin-bottom-8 ds-margin-top-8"
        />
      </div>
      <div
        v-if="showSummaryToogle"
        class="ds-grid__col ds-grid__col--12 ds-md-grid__col--4 ds-lg-grid__col--2 ds-margin-top-24 ds-md-margin-top-0"
        :class="{ 'ds-display-flex ds-align-items-center ds-justify-content-flex-end': !isScreenSmall }"
      >
        <div class="ds-margin-bottom-8 ds-margin-top-8">
          <Toggle
            v-model="showSummary"
            :label="labels.summarize"
            orientation="left"
          />
        </div>
      </div>
    </div>
    <!-- Social Bar -->
    <div
      class="kh-chapters-social-bar-wrapper"
      :class="{ 'sticky-top': isScreenLarge, 'ds-grid': !isScreenLarge }"
    >
      <DsSocialBar
        :disposition="isScreenLarge ? 'vertical' : 'horizontal'"
        :items="socialItems"
        :class="{
          'sticky-top--below-on-page-navigation': isScreenLarge,
          'ds-grid__col ds-grid__col--12': !isScreenLarge
        }"
        class="kh-chapters-social-bar ds-lg-padding-top-48 ds-padding-bottom-24"
      />
    </div>

    <!-- Summary mode -->
    <template v-if="showSummary">
      <div class="kh-chapters-summary ds-grid ds-padding-y-24 ds-md-padding-y-48">
        <div
          class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--8 ds-lg-grid__col--offset-2"
          :style="{ marginTop: isScreenLarge ? summaryMarginTop : '' }"
        >
          <div
            v-for="(summary, index) in summaries"
            :key="summary.id"
            class="ds-padding-48 ds-background-color-grey"
            :class="[
              index < summaries.length - 1 ? 'ds-margin-bottom-24 ds-md-margin-bottom-48' : 'ds-margin-bottom-0'
            ]"
          >
            <h2 class="ds-heading ds-margin-bottom-24 ds-md-margin-bottom-48">
              {{ summary.title }}
            </h2>
            <RichText :data="summary.description" />
          </div>
        </div>
      </div>
    </template>

    <!-- Full article mode -->
    <template v-else>
      <!-- Article Content: Intro text -->
      <div
        v-if="intro"
        class="kh-chapters-intro ds-grid ds-padding-y-24 ds-md-padding-y-48"
      >
        <div class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--8 ds-lg-grid__col--offset-2">
          <ClientOnly>
            <RichText :data="intro" />
          </ClientOnly>
        </div>
      </div>

      <!-- Article Content: Chapters -->
      <div
        v-for="(chapter, index) in chapters"
        :key="`chapter-${index}`"
        class="kh-chapter ds-grid ds-padding-y-24 ds-md-padding-y-48"
      >
        <!-- Article Content: Heading Video or Image -->
        <DsVideoPlayer
          v-if="chapter.video"
          v-bind="chapter.video"
          class="kh-chapter__heading-media ds-width-full ds-padding-bottom-48 ds-md-padding-bottom-96"
          :class="{ 'ds-grid__col ds-grid__col--12': isScreenLarge }"
          :screen="screen"
          :aria-label-close="labels.close"
          :aria-label-play="labels.play"
          is-footer-narrow
        />
        <DsImageWithFooter
          v-if="!chapter.video && chapter.image"
          :image="chapter.image"
          class="kh-chapter__heading-media ds-width-full ds-padding-bottom-48 ds-md-padding-bottom-96"
          :class="{ 'ds-grid__col ds-grid__col--12': isScreenLarge }"
          :screen="screen"
          is-footer-narrow
        />

        <!-- Article Content: Chapter Content -->
        <ClientOnly>
          <div
            class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--8 ds-lg-grid__col--offset-2"
            :data-id="slugify(chapter.navigationTitle || chapter.title)"
          >
            <h2 class="kh-chapter__header ds-heading">
              {{ chapter.title }}
            </h2>
            <RichText
              v-if="chapter.text"
              ref="chapters"
              :data="chapter.text"
              table-type="primary"
              class="ds-margin-top-24 ds-md-margin-top-48"
            />
          </div>
        </ClientOnly>
      </div>
    </template>
  </DsSection>
</template>

<script setup>
import { kebabCase } from 'lodash-es'
import { DsSection, DsVideoPlayer, DsImageWithFooter, DsSocialBar, DsTag, resizeAwds } from 'awds'

const { isScreenSmall, isScreenLarge, screen } = useScreen()
const router = useRouter()
const route = useRoute()

// Init directive
const vResizeAwds = resizeAwds()

const emit = defineEmits(['summary-changed'])

const props = defineProps({
  sidebarTags: { type: Array, default: () => [] },
  intro: { type: Object, default: null },
  chapters: { type: Array, default: () => [] },
  summaries: { type: Array, default: () => [] },
  labels: { type: Object, default: null },
  pageSeoData: { type: Object, default: null },
  isStickyPageNav: { type: Boolean, default: false }, // @TODO remove
  summaryMarginTop: { type: String, default: '' } // @TODO remove
})

const scrollPosition = ref(0)
const scrollDirection = ref(null)
const showSummary = ref(false)

const showSummaryToogle = computed(() => {
  return !!props.summaries?.length
})

const socialItems = computed(() => {
  const { url, title, description } = props.pageSeoData
  const items = [
    {
      iconName: 'Linkedin',
      text: `Linkedin ${props.labels.opensInNewWindow}`,
      url: 'https://www.linkedin.com/sharing/share-offsite/?url={{url}}'
    },
    {
      iconName: 'Twitter',
      text: `Twitter ${props.labels.opensInNewWindow}`,
      url: 'https://twitter.com/intent/tweet?text={{title}}&url={{url}}'
    },
    {
      iconName: 'Facebook',
      text: `Facebook ${props.labels.opensInNewWindow}`,
      url: 'https://www.facebook.com/sharer/sharer.php?u={{url}}&title={{title}}&description={{description}}'
    }
  ]
  return items.map((item) => ({
    ...item,
    url: item.url.replace('{{url}}', url).replace('{{title}}', title).replace('{{description}}', description)
  }))
})

watch(showSummary, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    router.push({ query: { summarize: newValue } })
    emit('summary-changed', newValue)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleWindowScroll)
})

onMounted(() => {
  window.addEventListener('scroll', handleWindowScroll)
  // Init summarize state
  setIsSummarize()
  // Init social bar position
  setSocialBarPosition()
})

const slugify = (name) => {
  return kebabCase(name)
}

// @TODO check if this is used
const handleWindowScroll = () => {
  if (document.body.getBoundingClientRect().top > scrollPosition.value) {
    scrollDirection.value = 'UP'
  } else {
    scrollDirection.value = 'DOWN'
  }
  scrollPosition.value = document.body.getBoundingClientRect().top
}

const handleResize = () => {
  nextTick(() => {
    setSocialBarPosition()
  })
}

const setSocialBarPosition = () => {
  // Set social bar margin left same value us next element (grid)
  // This needs to be done because grid elements has margin auto set
  const socialBar = document.querySelector('.kh-chapters-social-bar-wrapper')
  const nextElement = socialBar.nextElementSibling
  if (socialBar && nextElement) {
    const computedStyle = window.getComputedStyle(nextElement)
    socialBar.style.marginLeft = computedStyle.marginLeft
  }
}

const setIsSummarize = () => {
  if (route.query?.summarize) {
    showSummary.value = JSON.parse(route.query?.summarize)
  } else {
    showSummary.value = false
  }
  emit('summary-changed', showSummary.value)
}
</script>
<style lang="scss" scoped>
@import 'awds/foundation-vars.scss';
@import 'awds/foundation-mixins.scss';

$height-on-page-navigation: 72px;
$height-social-bar: 174px;

.kh-chapters {
  @include wds-screen('large') {
    &:deep(.ds-container) {
      position: relative;
    }

    .kh-chapters-social-bar-wrapper {
      height: calc(100% - #{$spacing-48} - #{$height-on-page-navigation});
      margin-left: $spacing-96;
      padding-left: $spacing-12;
      position: absolute;
      z-index: 0;
    }

    .kh-chapters-social-bar {
      top: calc(50vh - #{$height-social-bar}) !important;
    }
  }
}
</style>
